<script setup lang="ts">
import { computed, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { PeopleService } from '@/services';
import { PersonStores } from '@/store';

const profileStore = PersonStores.useUserProfileStore();

const toast = useToast();

const openActivate = ref(false);

const isActivated = computed<boolean>(() => {
  const person = profileStore.person;
  return !!person?.user?.can_login && !person?.archived_at;
});

const hasEmail = computed<boolean>(() => profileStore.general?.email);

const iconType = computed(() => (!hasEmail.value ? 'warning' : 'info'));

const iconColor = computed(() => [
  {
    color: hasEmail.value ? 'grey' : 'orange',
  },
]);

const activateMessage = computed(() =>
  hasEmail.value
    ? 'This user will be sent an email with a link which will allow them to set a password and sign in to their account.'
    : 'An email address is required to activate access. Please add a valid email address in the profile section of the user.',
);

const onCancel = () => (openActivate.value = false);

const onOpenActivate = () => (openActivate.value = true);

const onActivate = async () => {
  await PeopleService.activateAccess(Number(profileStore.person.id))
    .then((response) => doSuccess(response.data.message))
    .catch((error) => doFail(error.data.message));

  openActivate.value = false;
};

const doSuccess = (detail: string) => {
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: detail,
    life: 3000,
  });
};

const doFail = (detail: string) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: detail,
    life: 3000,
  });
};
</script>

<template>
  <div v-if="!isActivated">
    <ConfirmAction
      v-if="openActivate"
      header="Activate Access"
      :show-footer="hasEmail"
      accept-label="Activate Access"
      @accept="onActivate"
      @cancel="onCancel"
    >
      <div class="flex text-gray-800">
        <Icon :name="iconType" :style="iconColor" />
        <span class="w-30rem pl-3" v-text="activateMessage" />
      </div>
    </ConfirmAction>
    <AppButton type="primary" text="Send Access Activation Email" icon-left="send" @click="onOpenActivate" />
  </div>
</template>
